import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AIFieldContext } from './AIAssistantContexts';
import { generateFAQs, assessSituation, SourceDocuments, UserFeedback } from '../utils/faqApi';
import FeedbackWidget from './FeedbackWidget';
import HistoryView, { HistoryItem } from './HistoryView';

interface FAQAssistProps {
  topic: string;
  fieldContext?: AIFieldContext; // Optional for RAG testing pages
  clearanceLevel?: string; // Optional prop for RAG testing pages
}

// Citation can be either a string (legacy) or structured object
interface StructuredCitation {
  text: string;
  source_id: string;
  page: number;
}

type Citation = string | StructuredCitation;

interface FAQItem {
  question: string;
  answer: string;
  citations: Citation[];
}

interface CustomAssessment {
  determination: string;
  confidence: number;
  reasoning: string;
  citations: Citation[];
  recommendation: string;
}

// Default field context for RAG testing when none is provided
const DEFAULT_FIELD_CONTEXT: AIFieldContext = {
  fieldType: 'general',
  fieldDescription: 'General information field',
  significance: 'Standard',
  commonRejectionReasons: [],
  interviewQuestions: [],
  finalPrompt: '',
  helpText: '',
  bestPractices: [],
  warningFlags: [],
  expectedLength: 'moderate',
  requiresSpecificity: false
};

const FAQAssist: React.FC<FAQAssistProps> = ({ topic, fieldContext = DEFAULT_FIELD_CONTEXT }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeHover, setActiveHover] = useState<number | null>(null);
  const [showCustomAssessment, setShowCustomAssessment] = useState(false);
  const [customSituation, setCustomSituation] = useState('');
  const [customAssessment, setCustomAssessment] = useState<CustomAssessment | null>(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [sourceDocuments, setSourceDocuments] = useState<SourceDocuments>({});
  const [feedbackRequestId, setFeedbackRequestId] = useState<string | null>(null);
  const [feedbackInteractionType, setFeedbackInteractionType] = useState<string>('');
  const [feedbackTopic, setFeedbackTopic] = useState<string>('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [faqsExpanded, setFaqsExpanded] = useState(true); // FAQs expanded by default
  const [faqPage, setFaqPage] = useState(0); // Current page of FAQs (0-based)
  const [searchFilter, setSearchFilter] = useState(''); // Search filter text
  const [searchExpanded, setSearchExpanded] = useState(false); // Search input visibility

  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Filter and paginate FAQs
  const filteredFaqs = faqs.filter(faq =>
    searchFilter.trim() === '' ||
    faq.question.toLowerCase().includes(searchFilter.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const FAQS_PER_PAGE = 3;
  const totalPages = Math.ceil(filteredFaqs.length / FAQS_PER_PAGE);
  const displayedFaqs = filteredFaqs.slice(
    faqPage * FAQS_PER_PAGE,
    (faqPage + 1) * FAQS_PER_PAGE
  );

  // Show controls only when we have more than 3 FAQs (cached mode)
  const showControls = faqs.length > 3;

  // Reset pagination when search filter changes
  useEffect(() => {
    setFaqPage(0);
  }, [searchFilter]);

  // Handle cycling through FAQ pages
  const handleRefreshFaqs = () => {
    setFaqPage((prevPage) => (prevPage + 1) % totalPages);
  };

  const loadFAQs = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Loading FAQs using new API...');
      // Request 9 FAQs - if cached, we'll get all 9; if API call, we'll get 3
      const response = await generateFAQs(topic, 9);
      console.log('FAQ Response:', response);

      if (response.success && response.guidance && response.guidance.faqs) {
        setFaqs(response.guidance.faqs);
        setSourceDocuments(response.source_documents || {});
        console.log('FAQs loaded:', response.guidance.faqs);
        console.log('Source documents:', response.source_documents);
      } else {
        console.error('Invalid FAQ response structure:', response);
      }
    } catch (error) {
      console.error('Error loading FAQs:', error);
      // Fallback FAQs
      setFaqs([
        {
          question: "What constitutes a foreign contact?",
          answer: "A foreign contact is any individual who is not a U.S. citizen and with whom you have more than casual contact.",
          citations: ["DCSA SEAD 3 Contact and Relationship Reporting Exercise"]
        },
        {
          question: "How often must I report foreign contacts?",
          answer: "Foreign contacts must be reported within 30 days of establishing the relationship and updated annually.",
          citations: ["DCSA Foreign Contact Reporting Guidelines"]
        },
        {
          question: "Do I need to report business contacts?",
          answer: "Yes, foreign business contacts that involve regular interaction or potential influence must be reported.",
          citations: ["DCSA SEAD 3 Contact and Relationship Reporting Exercise"]
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load FAQs when component opens
  useEffect(() => {
    if (isOpen && faqs.length === 0) {
      loadFAQs();
    }
  }, [isOpen, faqs.length, loadFAQs]);

  // Load history from localStorage on mount
  useEffect(() => {
    const storedHistory = localStorage.getItem('faq_history');
    if (storedHistory) {
      try {
        const parsed = JSON.parse(storedHistory);
        setHistory(parsed);
      } catch (error) {
        console.error('Failed to load history from localStorage:', error);
      }
    }
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomAssessment(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Auto-focus textarea when custom assessment opens
  useEffect(() => {
    if (showCustomAssessment && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showCustomAssessment]);

  const addToHistory = (
    requestId: string,
    question: string,
    answer: string,
    blocked?: boolean
  ) => {
    const newItem: HistoryItem = {
      request_id: requestId,
      timestamp: new Date().toISOString(),
      question,
      answer,
      blocked
    };

    const updatedHistory = [...history, newItem];
    setHistory(updatedHistory);
    localStorage.setItem('faq_history', JSON.stringify(updatedHistory));
  };

  const updateHistoryRating = (requestId: string, rating: number) => {
    const updatedHistory = history.map(item =>
      item.request_id === requestId ? { ...item, rating } : item
    );
    setHistory(updatedHistory);
    localStorage.setItem('faq_history', JSON.stringify(updatedHistory));
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('faq_history');
  };

  const handleCustomAssessment = async () => {
    if (!customSituation.trim()) return;

    const currentQuestion = customSituation;

    // Clear previous response and feedback to show new activity
    setCustomAssessment(null);
    setFeedbackRequestId(null);

    setAssessmentLoading(true);
    try {
      console.log('Assessing situation using new API...');
      const response = await assessSituation(currentQuestion, topic);
      console.log('Assessment Response:', response);

      if (response.success && response.guidance && response.guidance.assessment) {
        setCustomAssessment(response.guidance.assessment);
        setSourceDocuments(response.source_documents || {});

        // Add to history
        if (response.request_id) {
          addToHistory(
            response.request_id,
            currentQuestion,
            response.guidance.assessment.determination,
            false
          );
        }

        // Capture feedback request info
        if (response.feedback_request) {
          setFeedbackRequestId(response.feedback_request.request_id);
          setFeedbackInteractionType(response.feedback_request.interaction_type);
          setFeedbackTopic(response.feedback_request.topic);
        }
      } else if (response.blocked) {
        // Guardrail intervention - show user-friendly blocked message
        console.log('Request blocked by guardrail:', response.block_type, response.block_reason);
        const blockedMessage = {
          determination: 'Unable to process this request',
          confidence: 0.0,
          reasoning: response.guidance?.assessment?.reasoning || 'Your question contains content that cannot be processed due to security policies. Please rephrase your question without sensitive details and try again.',
          citations: [],
          recommendation: 'Try rephrasing your question in general terms, or contact your Facility Security Officer (FSO) for direct assistance.'
        };
        setCustomAssessment(blockedMessage);

        // Add blocked interaction to history
        if (response.request_id) {
          addToHistory(
            response.request_id,
            currentQuestion,
            blockedMessage.determination,
            true
          );
        }

        // Capture feedback request even for blocked responses (useful data)
        if (response.feedback_request) {
          setFeedbackRequestId(response.feedback_request.request_id);
          setFeedbackInteractionType(response.feedback_request.interaction_type);
          setFeedbackTopic(response.feedback_request.topic);
        }
      }
    } catch (error) {
      console.error('Error getting custom assessment:', error);
    } finally {
      setAssessmentLoading(false);
    }
  };

  // Helper function to get citation text and URI
  const getCitationInfo = (citation: Citation): { text: string; uri: string | null } => {
    // Structured citation with source_id - O(1) lookup!
    if (typeof citation === 'object' && 'source_id' in citation) {
      const doc = sourceDocuments[citation.source_id];
      const displayName = doc?.display_name || doc?.document_name || 'Unknown Source';
      const pageText = citation.page ? `, page ${citation.page}` : '';
      const sourceAttribution = `${displayName}${pageText}`;

      // Check if citation.text already contains the document name
      const citationTextLower = citation.text.toLowerCase();
      const docNameLower = displayName.toLowerCase();
      const alreadyHasDocName = citationTextLower.includes(docNameLower);

      let displayText: string;
      if (alreadyHasDocName) {
        // Citation text already has document name - use it as-is
        displayText = citation.text;
      } else {
        // Citation text is a quote/excerpt - append source attribution
        // Format: "Quote text" (Document Name, page X)
        displayText = `"${citation.text}" (${sourceAttribution})`;
      }

      return {
        text: displayText,
        uri: doc?.uri || null
      };
    }

    // Legacy string citation - search through values
    const citationText = citation as string;
    const source = Object.values(sourceDocuments).find(doc =>
      doc.display_name === citationText || doc.document_name === citationText
    );
    return {
      text: citationText,
      uri: source?.uri || null
    };
  };


  const handleMouseEnter = (index: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveHover(index);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveHover(null);
    }, 300); // Small delay to allow moving to answer popup
  };

  const handleAnswerMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleAnswerMouseLeave = () => {
    setActiveHover(null);
  };

  const handleFeedbackSubmit = (feedback: UserFeedback) => {
    console.log('Feedback submitted:', feedback);

    // Update history with rating
    updateHistoryRating(feedback.request_id, feedback.rating);

    // TODO: When feedback endpoint is ready, send feedback
    // This data can be correlated with the audit_payload via request_id
    // For now, it's logged to console for development

    // In future, send to feedback endpoint:
    // await fetch(feedbackEndpointUrl, {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(feedback)
    // });

    // Could also store in localStorage for analytics
    const storedFeedback = JSON.parse(localStorage.getItem('user_feedback') || '[]');
    storedFeedback.push(feedback);
    localStorage.setItem('user_feedback', JSON.stringify(storedFeedback));
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* FAQ Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-md hover:bg-purple-200 transition-colors"
        title="Frequently Asked Questions"
      >
        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        FAQ
      </button>

      {/* FAQ Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Foreign Contact FAQ</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Collapsible FAQ List */}
                <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                  {/* FAQ Header */}
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <button
                      onClick={() => setFaqsExpanded(!faqsExpanded)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${faqsExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Common Questions ({faqs.length})
                    </button>

                    {/* Search and Refresh Controls (only show when >3 FAQs) */}
                    {showControls && (
                      <div className="flex items-center gap-2">
                        {/* Search - Collapsible */}
                        {searchExpanded ? (
                          <div className="relative flex items-center">
                            <input
                              type="text"
                              value={searchFilter}
                              onChange={(e) => setSearchFilter(e.target.value)}
                              placeholder="Search FAQs..."
                              className="w-40 pl-2 pr-6 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                setSearchExpanded(false);
                                setSearchFilter('');
                              }}
                              className="absolute right-1 p-0.5 text-gray-400 hover:text-gray-600 rounded"
                              title="Close search"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setSearchExpanded(true)}
                            className="p-1 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                            title="Search FAQs"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        )}

                        {/* Refresh/Cycle Button */}
                        <button
                          onClick={handleRefreshFaqs}
                          className="p-1 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="Show next 3 FAQs"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* FAQ Items */}
                  {faqsExpanded && (
                    <div className="p-2 space-y-1">
                      {displayedFaqs.length > 0 ? (
                        displayedFaqs.map((faq, index) => (
                          <div key={index} className="relative">
                            <button
                              className="w-full text-left p-2 text-sm text-purple-700 hover:bg-purple-50 rounded transition-colors"
                              onMouseEnter={() => handleMouseEnter(index)}
                              onMouseLeave={handleMouseLeave}
                            >
                              {faq.question}
                            </button>

                            {/* Answer Popup */}
                            {activeHover === index && (
                              <div
                                className="absolute left-full top-0 ml-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50"
                                onMouseEnter={handleAnswerMouseEnter}
                                onMouseLeave={handleAnswerMouseLeave}
                              >
                                <p className="text-sm text-gray-700 mb-3">{faq.answer}</p>
                                {faq.citations.length > 0 && (
                                  <div className="border-t border-gray-200 pt-2">
                                    <p className="text-xs text-gray-500 mb-1">Sources:</p>
                                    {faq.citations.map((citation, idx) => {
                                      const { text, uri } = getCitationInfo(citation);
                                      return uri ? (
                                        <a
                                          key={idx}
                                          href={uri}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:text-blue-800 underline block"
                                        >
                                          {text}
                                        </a>
                                      ) : (
                                        <p key={idx} className="text-xs text-blue-600">{text}</p>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-gray-500">
                          No FAQs match your search
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* History View */}
                {history.length > 0 && (
                  <div className="mt-4">
                    <HistoryView
                      history={history}
                      onClearHistory={clearHistory}
                    />
                  </div>
                )}

                {/* Custom Assessment Option */}
                <div className="border-t border-gray-200 pt-3 mt-4">
                  <button
                    onClick={() => {
                      setShowCustomAssessment(!showCustomAssessment);
                      // Auto-collapse FAQs when user enters custom assessment mode
                      if (!showCustomAssessment) {
                        setFaqsExpanded(false);
                      }
                    }}
                    className="w-full text-left p-2 text-sm text-blue-700 hover:bg-blue-50 rounded transition-colors font-medium"
                  >
                    Describe your situation for personalized assessment
                  </button>

                  {showCustomAssessment && (
                    <div className="mt-3 space-y-3">
                      <textarea
                        ref={textareaRef}
                        value={customSituation}
                        onChange={(e) => {
                          setCustomSituation(e.target.value);
                          // Auto-collapse FAQs when user starts typing
                          if (e.target.value.length > 0 && faqsExpanded) {
                            setFaqsExpanded(false);
                          }
                        }}
                        placeholder="Describe your foreign contact situation..."
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                      />
                      <button
                        onClick={handleCustomAssessment}
                        disabled={!customSituation.trim() || assessmentLoading}
                        className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {assessmentLoading ? 'Assessing...' : 'Get Assessment'}
                      </button>

                      {customAssessment && (
                        <div className={`p-3 rounded border ${
                          customAssessment.confidence === 0 ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'
                        }`}>
                          {customAssessment.confidence === 0 && (
                            <div className="flex items-start mb-2">
                              <svg className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <span className="text-xs font-medium text-amber-800">Content Restriction</span>
                            </div>
                          )}
                          <div className="mb-2">
                            <span className="text-xs font-medium text-gray-700">Determination: </span>
                            <span className="text-xs text-gray-900">{customAssessment.determination}</span>
                          </div>
                          {customAssessment.confidence > 0 && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-gray-700">Confidence: </span>
                              <span className={`text-xs font-medium ${
                                customAssessment.confidence >= 0.8 ? 'text-green-600' :
                                customAssessment.confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {Math.round(customAssessment.confidence * 100)}%
                              </span>
                            </div>
                          )}
                          <p className="text-xs text-gray-700 mb-2">{customAssessment.reasoning}</p>
                          {(customAssessment.confidence < 0.7 || customAssessment.confidence === 0) && customAssessment.recommendation && (
                            <div className={`p-2 rounded ${
                              customAssessment.confidence === 0 ? 'bg-amber-100 border border-amber-300' : 'bg-yellow-50 border border-yellow-200'
                            }`}>
                              <p className={`text-xs ${
                                customAssessment.confidence === 0 ? 'text-amber-900' : 'text-yellow-800'
                              }`}>
                                <strong>Recommendation:</strong> {customAssessment.recommendation}
                              </p>
                            </div>
                          )}
                          {customAssessment.citations.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Sources:</p>
                              {customAssessment.citations.map((citation, idx) => {
                                const { text, uri } = getCitationInfo(citation);
                                return uri ? (
                                  <a
                                    key={idx}
                                    href={uri}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:text-blue-800 underline block"
                                  >
                                    {text}
                                  </a>
                                ) : (
                                  <p key={idx} className="text-xs text-blue-600">{text}</p>
                                );
                              })}
                            </div>
                          )}

                          {/* Feedback Widget */}
                          {feedbackRequestId && (
                            <FeedbackWidget
                              requestId={feedbackRequestId}
                              interactionType={feedbackInteractionType}
                              topic={feedbackTopic}
                              onSubmit={handleFeedbackSubmit}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQAssist;